import { css } from '@emotion/react';
import { FormikContext, useFormik } from 'formik';
import * as _ from 'lodash';
import * as React from 'react';

import { GetSensitiveAccessTokenParams, MFAFactor, MFAFactorState } from '@journeyapps-platform/types-accounts-hub';
import { observer } from 'mobx-react';
import { Keys, useKeyPress } from '../../hooks/useKeyPress';
import { useSystem } from '../../hooks/useSystem';
import { SMS_OTP_CODE_LENGTH } from '../../pages/profile/security/mfa/factors/sms/SMSMFATestWidget';
import { styled } from '../../theme';
import { LoaderBackdrop, LoaderSpinner } from '../LoaderWidget';
import { ButtonType } from '../buttons/ButtonWidget';
import { GroupedButtons } from '../buttons/GroupedButtons';
import { FormikInputWidget } from '../inputs/FormikInput';
import { CenterFormOrientation, CenterFormWidget } from '../layout/CenterFormWidget';
import { FormLayoutWidget } from '../layout/FormLayoutWidget';
import { FormikOTPAuthenticationWidget } from './FormikOTPAuthenticationWidget';

export type SensitiveUserAuthWidgetProps = {
  onCancel: () => void;
  onAuthenticated: (token: string) => void;
};

/**
 * User's with no MFA have to enter their password
 */
export const SensitiveAuthenticationMethod = {
  ...MFAFactor,
  PASSWORD: 'password'
} as const;

export type SensitiveAuthenticationMethod =
  (typeof SensitiveAuthenticationMethod)[keyof typeof SensitiveAuthenticationMethod];

namespace S {
  const BASE_LABEL = 'sensitive-profile-auth-widget';

  const MIN_DIMENSIONS = css`
    min-height: 250px;
    width: 400px;
  `;

  export const Message = styled.div`
    label: ${BASE_LABEL}-message;
    font-size: 14px;
    color: ${(p) => p.theme.text.secondary};
    text-align: center;
  `;

  export const FormContent = styled.div`
    ${MIN_DIMENSIONS}
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  `;

  export const SpreadLoaderBackdrop = styled(LoaderBackdrop)`
    ${MIN_DIMENSIONS}
  `;

  export const PasswordInput = styled(FormikInputWidget)`
    flex-grow: 0;
    width: 100%;
  `;

  export const PasswordMessageContainer = styled.div`
    margin-top: 30px;
  `;

  export const Container = styled.div``;

  export const Resend = styled.span`
    font-weight: bold;
    cursor: pointer;
  `;
}

export const SensitiveUserAuthWidget: React.FC<SensitiveUserAuthWidgetProps> = observer((props) => {
  const system = useSystem();

  const { mfaStore } = system;
  const { preferredMFAFactor } = mfaStore;

  const [loadingPreferences, setLoadingPreferences] = React.useState(true);

  const [authenticationMethod, setAuthenticationMethod] = React.useState(
    preferredMFAFactor?.factorType ?? SensitiveAuthenticationMethod.PASSWORD
  );

  const formikInputField: keyof GetSensitiveAccessTokenParams =
    authenticationMethod == SensitiveAuthenticationMethod.PASSWORD ? 'password' : 'otp_attempt';

  const formik = useFormik<GetSensitiveAccessTokenParams>({
    initialValues: { password: '', otp_attempt: '', factor_id: '' },
    validateOnChange: false,
    validateOnBlur: true,
    validate: async (values) => {
      if (!values.password && !values.otp_attempt) {
        return {
          [formikInputField]: 'This is required'
        };
      }

      if (values.otp_attempt && values.otp_attempt.length < SMS_OTP_CODE_LENGTH) {
        return {
          otp_attempt: 'Enter all digits to continue'
        };
      }
    },
    onSubmit: async (values, helpers) => {
      // Try and get super token
      try {
        const tokenResponse = await system.accountV5Client.getSensitiveAccessToken(values);
        props.onAuthenticated(tokenResponse.token);
        helpers.setFieldValue('password', '');
        helpers.setFieldValue('otp_attempt', '');
        return true;
      } catch (error) {
        console.error(error);
        helpers.setFieldError(formikInputField, 'Authentication failed. Please try again');
      }
      return false;
    }
  });

  const sendSMS = async () => {
    try {
      const factor = mfaStore.getActiveFactor(MFAFactor.SMS)!;
      const { sent } = await factor.sendSMS();
      if (!sent) {
        system.dialogStore.errorDialog({ errorMessage: 'Could not send SMS/Text message' });
      }
    } catch (error) {
      console.error(error);
      system.dialogStore.errorDialog({
        errorMessage:
          'Could not send SMS/Text message. Request are rate limited to 1 per minute. Please retry in a few minutes.'
      });
    }
  };

  // Load the user's MFA preference
  React.useEffect(() => {
    if (mfaStore.preferredMFAFactor) {
      // Can show the dialog already
      setLoadingPreferences(false);
    }

    // Reload just incase something changes elsewhere
    mfaStore.loadUserMFAConfig().then(() => {
      const { preferredMFAFactor } = mfaStore;
      if (preferredMFAFactor) {
        setAuthenticationMethod(preferredMFAFactor.factorType);
      }
      setLoadingPreferences(false);
    });
  }, []);

  React.useEffect(() => {
    let factorId: string;
    switch (authenticationMethod) {
      case SensitiveAuthenticationMethod.PASSWORD:
        factorId = '';
        break;
      case MFAFactor.SMS:
        sendSMS(); // don't break here, need to set factor ID
      default:
        factorId =
          _.values(mfaStore.models).find(
            (factorModel) =>
              factorModel.model.state == MFAFactorState.ACTIVE && factorModel.factorType == authenticationMethod
          )?.id ?? '';
    }
    formik.setFieldValue('factor_id', factorId);
  }, [authenticationMethod]);

  const nextMFAOptions = _.chain(mfaStore.models)
    .values()
    .filter(
      (factorModel) =>
        factorModel.model.state == MFAFactorState.ACTIVE && factorModel.factorType !== authenticationMethod
    )
    .value();

  const generateInputContent = () => {
    switch (authenticationMethod) {
      case SensitiveAuthenticationMethod.PASSWORD:
        return (
          <>
            <S.PasswordInput
              name="password"
              input={{
                type: 'password',
                placeholder: 'Password'
              }}
            />
            <S.PasswordMessageContainer>
              <S.Message>Your password is required in order to continue</S.Message>
            </S.PasswordMessageContainer>
          </>
        );
      case SensitiveAuthenticationMethod.TOTP:
      case SensitiveAuthenticationMethod.SMS:
        return (
          <S.Container>
            <FormikOTPAuthenticationWidget
              name="otp_attempt"
              disabled={formik.isSubmitting}
              otpLength={SMS_OTP_CODE_LENGTH} // Should always be the same as SMS
              error={formik.errors.otp_attempt}
              enableBackupCodes={true}
              alternativeActions={nextMFAOptions.map((option) => {
                return {
                  label: `Try ${option.name}`,
                  action: () => setAuthenticationMethod(option.factorType)
                };
              })}
              messageContent={() => {
                return (
                  <S.Container>
                    {authenticationMethod == SensitiveAuthenticationMethod.SMS ? (
                      <S.Message>
                        We've sent an SMS/text message to your phone. Enter the code to continue. Didn't get it?{' '}
                        <S.Resend onClick={() => sendSMS()}>Resend SMS/text message</S.Resend>{' '}
                      </S.Message>
                    ) : (
                      <S.Message>Enter a code from your authenticator app to continue.</S.Message>
                    )}
                  </S.Container>
                );
              }}
            />
          </S.Container>
        );
    }
  };

  useKeyPress(Keys.ENTER, {
    onKeyPressed: () => {
      formik.submitForm();
    }
  });

  return (
    <S.Container>
      <FormikContext.Provider value={formik}>
        <CenterFormWidget
          noLogo={true}
          primaryLabel={'Authentication Required'}
          orientation={CenterFormOrientation.ROW}
        >
          {loadingPreferences ? (
            <S.SpreadLoaderBackdrop>
              <LoaderSpinner />
            </S.SpreadLoaderBackdrop>
          ) : (
            <FormLayoutWidget
              sections={[
                {
                  key: 'input',
                  elements: [<S.FormContent key="input-content">{generateInputContent()}</S.FormContent>]
                },
                {
                  key: 'submit',
                  elements: [
                    <GroupedButtons
                      key="buttons"
                      btns={[
                        {
                          label: 'Cancel',
                          type: ButtonType.SECONDARY,
                          action: props.onCancel
                        },
                        {
                          label: 'Continue',
                          loadingOverride: formik.isSubmitting,
                          action: () => formik.submitForm()
                        }
                      ]}
                    />
                  ]
                }
              ]}
            />
          )}
        </CenterFormWidget>
      </FormikContext.Provider>
    </S.Container>
  );
});
